/**
 * @module
 *
 * @import {Vote} from '../service/votes';
 */

import {AttributeLink, AttributeSetup, task} from '@acng/frontend-stargazer';
import {GlobalActor, globalValue, Watch} from '@acng/frontend-relativity';
import {ARRAY, guard, STRING} from '@acng/frontend-rubicon';
import {iso, now} from '@acng/frontend-bounty/std/date.js';
import {HIDDEN, set, unset} from '@acng/frontend-bounty/dom/attribute.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {all} from '@acng/frontend-bounty/std/control.js';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {clearTimeout, setTimeout, window} from '@acng/frontend-bounty';
import {parseJSON} from '@acng/frontend-bounty/parse.js';
import {parseInteger} from '@acng/frontend-bounty/std/number.js';

import {TIMING_END, TIMING_START} from '../../layout/attribute/common.js';
import {cdn} from '../../core/service/cdn.js';
import {ctxVotables, ctxVotes} from '../context/list.js';
import {setup} from '../service/votes.js';
import {feature} from '../service/template.js';
import {inject, isInjectable} from 'acng/core/service/ng.js';
import {onlineFirst} from 'acng/amateurPool/service/online.js';

/**
 * @group DOM Attribute
 */
const name = 'hotornot-timer';

AttributeSetup(name, () => {
  // @ts-ignore won't annotate that actionslider module
  const actionslider = isInjectable('Actionslider') ? inject('Actionslider') : null;
  const {localStorage} = window;
  /**
   * @type {Amateur['id'][]}
   */
  let global = parseJSON(localStorage.getItem(`${name}-list`) ?? '[]');
  let expire = parseInteger(localStorage.getItem(`${name}-expire`) ?? `${now()}`);
  const lifetime = 6e4 * 10;
  const provideVotables = GlobalActor(ctxVotables);
  const watchVotes = Watch(attributeSelector(name), ctxVotes);

  const reload = async () => {
    expire = now() + lifetime;
    const user = inject('user');
    const url = `/api/hotornot/votables?fsk=${user.fsk}&niche=${user.niche}&sorting_suffix=${user.list_sorting_suffix}`;
    const [list, votes] = await all([cdn(url), setup()]);

    ASSERT: guard(list, ARRAY(STRING), `${url} response`);
    global = filter(await onlineFirst(list), votes);
    localStorage.setItem(`${name}-list`, JSON.stringify(global));
    localStorage.setItem(`${name}-expire`, JSON.stringify(expire));
  };

  /**
   * @param {Amateur['id'][]} list
   * @param {Vote[]} votes
   */
  const filter = (list, votes) =>
    list.filter((id) => !votes.some((vote) => vote.amateur_id == parseInt(id))).slice(0, 10);

  AttributeLink(name, () => (element) => {
    /**
     * @param {Element} element
     * @param {Vote[]} votes
     */
    const update = (element, votes) => {
      const list = filter(global, votes);

      if (list.length) {
        unset(element, TIMING_END);
      } else {
        set(element, TIMING_END, iso(expire));
        set(element, TIMING_START, '0');
      }

      provideVotables(list);
    };

    const startTimeout = () =>
      setTimeout(async () => {
        await reload();
        await update(element, globalValue(ctxVotes));
        timeout = startTimeout();
      }, expire - now());

    let timeout = startTimeout();

    watchVotes(element, update);
    actionslider?.hide();

    return () => {
      actionslider?.show();
      clearTimeout(timeout);
    };
  });

  return (element, value, compiler) => {
    if (feature[HIDDEN]) {
      remove(element);
    } else if (expire <= now()) {
      task(compiler, reload());
    }
  };
});

export {name as HOTORNOT_TIMER};
